import type { PdfConfirmFieldName, PdfConfirmFieldValue } from '../../types/pdfPreview';
import { PDF_AMENITY_FIELDS } from '../../types/pdfPreview';

// Shared by Preview controls and the Confirm preflight; values match backend enums.
export const pdfConfirmFieldOptions: Partial<Record<PdfConfirmFieldName, string[]>> = {
  activityKind: ['sale', 'rent'],
  propertyKind: [
    'apartment',
    'townhouse',
    'villa',
    'floor',
    'penthouse',
    'residentialLand',
    'villaCompound',
    'hotelApartment',
    'commercialVilla',
    'warehouse',
    'industrialLand',
    'commercialLand',
    'office',
    'labourCamp',
    'commercialBuilding',
    'shop',
  ],
  transactionType: ['ready', 'resale', 'distress'],
  completionStatus: ['ready', 'off-plan'],
  furniture: ['furnished', 'partly furnished', 'unfurnished'],
  floor: ['low', 'middle', 'high'],
  beds: ['Studio', '1', '2', '3', '4', '5', '6', '7', '8+'],
  baths: ['1', '2', '3', '4', '5', '6', '7', '8+'],
  currency: ['AED', 'USD'],
  defaultRentFrequency: ['yearly', 'monthly', 'weekly', 'daily'],
  handoverQuarter: ['Q1', 'Q2', 'Q3', 'Q4'],
};

const priceFields = new Set<PdfConfirmFieldName>([
  'price',
  'sellingPrice',
  'priceDaily',
  'priceWeekly',
  'priceMonthly',
  'priceYearly',
]);
const areaFields = new Set<PdfConfirmFieldName>(['builtUpArea', 'plotArea']);
const maxInt = 2147483647;

export const validPdfConfirmField = (
  field: PdfConfirmFieldName,
  value: PdfConfirmFieldValue
): boolean => {
  if (value === null) return true;
  const options = pdfConfirmFieldOptions[field];
  if (options) return typeof value === 'string' && options.includes(value);
  if (field === 'locationId')
    return (
      typeof value === 'string' &&
      /^[1-9]\d{0,18}$/.test(value) &&
      (value.length < 19 || value <= '9223372036854775807')
    );
  if (field === 'handoverYear') return typeof value === 'string' && /^\d{4}$/.test(value);
  if (field === 'title' || field === 'description')
    return typeof value === 'string' && value.length <= (field === 'title' ? 256 : 8000);
  if (areaFields.has(field))
    return typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= maxInt;
  if (priceFields.has(field))
    return typeof value === 'string' && /^(0|[1-9]\d{0,9})$/.test(value) && Number(value) <= maxInt;
  if (field === 'view')
    return (
      Array.isArray(value) &&
      value.length <= 32 &&
      value.every(
        (item) => typeof item === 'string' && item.length <= 120 && !/https?:|@/.test(item)
      )
    );
  if (field === 'postHandover' || (PDF_AMENITY_FIELDS as readonly string[]).includes(field))
    return typeof value === 'boolean';
  return false;
};
